/**
 * Every input of a Godot-editor viewport gesture (pointer, wheel, keyboard, per-frame
 * freelook) routed onto an `EditorControlsHandle`. The handlers share one effect, so
 * the cleanup detaches exactly the listeners it attached. The gestures themselves
 * live in `editorMouseNavigation.ts`, `editorTouchGesture.ts` and `editorKeyBindings.ts`.
 */
import { useFrame } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { isTypingTarget } from './hooks/isTypingTarget.js';
import { freelookMoveCursor, resolveNavMode } from './godotEditorCursor.js';
import { isGesturePointer } from './pointerGesture.js';
import { applyEditorDragMove, applyEditorWheel } from './editorMouseNavigation.js';
import { applyEditorTouchMove, beginEditorTouch, endEditorTouch, } from './editorTouchGesture.js';
import { applyEditorViewKey, consumeEditorViewKey, freelookKeysFrom, FREELOOK_KEYS, } from './editorKeyBindings.js';
export function useEditorNavigation(gl, handle, invalidate, get) {
    const dragRef = useRef(null);
    // Every touch pointer currently down, in the order it landed: a pinch needs
    // two at once, which a single drag slot cannot hold.
    const touchPointsRef = useRef(new Map());
    const touchGestureRef = useRef(null);
    const freelookRef = useRef(false);
    const heldKeysRef = useRef(new Set());
    const sprintRef = useRef(false);
    useEffect(() => {
        const element = gl.domElement;
        // Captured once, so the cleanup clears the Map the handlers filled, not
        // whatever `.current` holds by then.
        const touchPoints = touchPointsRef.current;
        // Godot ignores every navigation input while it previews a Camera3D
        // (`node_3d_editor_plugin.cpp:1826-1829`), so neither camera moves. A gesture
        // under way when a preview starts stops moving the camera with it.
        const isPreviewing = () => handle.isPreviewing(get().camera);
        /**
         * The invariant every touch path keeps: no fingers, no gesture origin. A stale
         * origin would make the next single-finger drag pan from where a pinch ended.
         */
        function resetTouch() {
            touchPoints.clear();
            touchGestureRef.current = null;
        }
        function endDrag() {
            const drag = dragRef.current;
            if (!drag)
                return;
            dragRef.current = null;
            freelookRef.current = false;
            heldKeysRef.current.clear();
            sprintRef.current = false;
            if (element.hasPointerCapture?.(drag.pointerId)) {
                element.releasePointerCapture(drag.pointerId);
            }
        }
        function endPointer(event) {
            if (isGesturePointer(event.pointerType)) {
                endEditorTouch(event, touchPoints, touchGestureRef);
                return;
            }
            endDrag();
        }
        function handlePointerDown(event) {
            if (isPreviewing())
                return;
            if (isGesturePointer(event.pointerType)) {
                beginEditorTouch(event, touchPoints, touchGestureRef);
                return;
            }
            if (dragRef.current)
                return;
            const mode = resolveNavMode(event.button, event);
            if (!mode)
                return;
            // Middle-click otherwise opens the platform's auto-scroll widget, which
            // eats every subsequent motion event.
            event.preventDefault();
            dragRef.current = {
                pointerId: event.pointerId,
                button: event.button,
                x: event.clientX,
                y: event.clientY,
            };
            freelookRef.current = mode === 'freelook';
            element.setPointerCapture?.(event.pointerId);
        }
        function handlePointerMove(event) {
            if (isPreviewing())
                return;
            if (isGesturePointer(event.pointerType)) {
                if (applyEditorTouchMove(event, touchPoints, touchGestureRef, handle))
                    invalidate();
                return;
            }
            const drag = dragRef.current;
            if (!drag)
                return;
            const moved = applyEditorDragMove(event, drag, handle, (freelook) => {
                freelookRef.current = freelook;
            });
            if (moved)
                invalidate();
        }
        function handleWheel(event) {
            event.preventDefault();
            if (isPreviewing())
                return;
            if (applyEditorWheel(event, handle, get))
                invalidate();
        }
        function handleContextMenu(event) {
            event.preventDefault();
        }
        function handleKeyDown(event) {
            if (isTypingTarget(event.target))
                return;
            if (isPreviewing()) {
                consumeEditorViewKey(event);
                return;
            }
            // Read the sprint modifier off the event rather than tracking its own
            // keydown: Shift held before freelook started never fires one.
            sprintRef.current = event.shiftKey;
            if (freelookRef.current && FREELOOK_KEYS[event.code]) {
                heldKeysRef.current.add(event.code);
                event.preventDefault();
                return;
            }
            if (applyEditorViewKey(event, handle))
                invalidate();
        }
        function handleKeyUp(event) {
            sprintRef.current = event.shiftKey;
            heldKeysRef.current.delete(event.code);
        }
        function handleBlur() {
            heldKeysRef.current.clear();
            sprintRef.current = false;
            resetTouch();
        }
        element.addEventListener('pointerdown', handlePointerDown);
        element.addEventListener('pointermove', handlePointerMove);
        element.addEventListener('pointerup', endPointer);
        element.addEventListener('pointercancel', endPointer);
        element.addEventListener('lostpointercapture', endPointer);
        element.addEventListener('contextmenu', handleContextMenu);
        // Not passive: a zoom must not also scroll the page behind the canvas.
        element.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);
        return () => {
            element.removeEventListener('pointerdown', handlePointerDown);
            element.removeEventListener('pointermove', handlePointerMove);
            element.removeEventListener('pointerup', endPointer);
            element.removeEventListener('pointercancel', endPointer);
            element.removeEventListener('lostpointercapture', endPointer);
            element.removeEventListener('contextmenu', handleContextMenu);
            element.removeEventListener('wheel', handleWheel);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
            endDrag();
            resetTouch();
        };
    }, [gl, handle, invalidate, get]);
    // Freelook flight is continuous while the keys are held, so it advances per
    // frame with the frame's own delta rather than per keydown repeat.
    useFrame((_, delta) => {
        if (!freelookRef.current || heldKeysRef.current.size === 0)
            return;
        if (handle.isPreviewing(get().camera))
            return;
        const keys = freelookKeysFrom(heldKeysRef.current, sprintRef.current);
        handle.applyCursor(freelookMoveCursor(handle.cursor(), keys, delta));
    });
}
//# sourceMappingURL=useEditorNavigation.js.map